import React, { useState } from 'react';
import AgencyNavbar from '../components/layout/AgencyNavbar';
import AgencyFooter from '../components/layout/AgencyFooter';
import { CheckCircle2, Star, Shield, MessageCircle, Zap, Rocket, Crown } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';

const PLAN_ICONS = { basico: Zap, pro: Rocket, premium: Crown };
const PLAN_ICON_COLORS = {
  basico: { bg: '#E9FF60', text: 'black' },
  pro:    { bg: '#FF3131', text: 'white' },
  premium:{ bg: '#E9FF60', text: 'black' }
};

const PlanesPage = () => {
  const { getPageContent, settings } = useSiteContent();
  const content = getPageContent('planes') || {};

  const [currency, setCurrency] = useState('USD');

  const title    = content.title    || 'PLANES Y PRECIOS';
  const subtitle = content.subtitle || 'Elegí el plan que mejor se adapte a tu negocio. Sin sorpresas, sin comisiones ocultas.';
  const guarantee = content.guarantee || {};

  const DEFAULT_PLANS = [
    {
      id: 'basico',
      name: 'Solución Directa',
      price_ars: 20000,
      price_usd: 20,
      description: 'Ideal para comenzar sin intermediarios',
      features: [
        'App con catálogo estándar',
        'Botón de WhatsApp integrado',
        'Soporte por mail',
        'Sistema de redirección de pedidos',
        'Validación por WhatsApp',
        'Base de datos de clientes',
        'Formulario autogestionable',
        'Estrategia de migración desde apps con comisiones'
      ],
      button_text: 'Solicitar Ahora',
      button_link_usd: '/demo',
      button_link_ars: '/demo',
      is_popular: false
    },
    {
      id: 'pro',
      name: 'Potenciá tus Pedidos',
      price_ars: 30000,
      price_usd: 30,
      description: 'Para negocios que quieren crecer',
      features: [
        'Todo lo del Nivel Básico',
        'Asesoría en Ads mensual',
        'Personalización estética visual',
        'Colores de tu marca',
        'Logo personalizado',
        'Optimización de conversión',
        'Reportes mensuales de performance'
      ],
      button_text: 'Solicitar Ahora',
      button_link_usd: '/demo',
      button_link_ars: '/demo',
      is_popular: true
    },
    {
      id: 'premium',
      name: 'Premium',
      price_ars: 60000,
      price_usd: 60,
      description: 'Máxima personalización y soporte',
      features: [
        'Todo lo del Nivel Pro',
        'Personalización tipográfica avanzada',
        'Dominio propio (opcional)',
        'Automatización de marketing promocional',
        'Automatización de base de datos',
        'Soporte prioritario 24/7',
        'Integración con herramientas externas',
        'Estrategia de fidelización de clientes'
      ],
      button_text: 'Solicitar Ahora',
      button_link_usd: '/demo',
      button_link_ars: '/demo',
      is_popular: false
    }
  ];

  // Use DB plans if they have the 3-plan structure, otherwise fallback to defaults
  const rawPlans = content.plans;
  const plans = (Array.isArray(rawPlans) && rawPlans.length === 3) ? rawPlans : DEFAULT_PLANS;

  const formatPrice = (plan) => {
    if (currency === 'USD') return `$${plan.price_usd}`;
    return `$${(plan.price_ars || 0).toLocaleString('es-AR')}`;
  };

  const getButtonLink = (plan) => {
    return currency === 'USD' 
      ? (plan.button_link_usd || plan.button_link || '/demo')
      : (plan.button_link_ars || plan.button_link || '/demo');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <AgencyNavbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mb-14">
            <h1
              className="text-5xl md:text-6xl font-black mb-4"
              style={{ fontFamily: 'Unbounded, Montserrat, sans-serif', color: '#E9FF60' }}
              data-testid="planes-title"
            >
              {title}
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8" data-testid="planes-subtitle">
              {subtitle}
            </p>

            {/* Currency Toggle */}
            <div className="inline-flex items-center gap-4 bg-[#1a1a1a] border border-[#333] rounded-full px-6 py-3">
              <span className={`text-sm font-bold transition-colors ${currency === 'ARS' ? 'text-[#E9FF60]' : 'text-gray-500'}`}>
                PESOS
              </span>
              <button
                onClick={() => setCurrency(currency === 'ARS' ? 'USD' : 'ARS')}
                className="relative w-14 h-7 rounded-full bg-[#333] transition-colors"
                data-testid="currency-toggle"
              >
                <div
                  className={`absolute top-1 w-5 h-5 rounded-full bg-[#E9FF60] transition-transform ${
                    currency === 'USD' ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-bold transition-colors ${currency === 'USD' ? 'text-[#E9FF60]' : 'text-gray-500'}`}>
                USD
              </span>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16 items-start">
            {plans.map((plan, i) => {
              const iconId = plan.id || (i === 0 ? 'basico' : i === 1 ? 'pro' : 'premium');
              const PlanIcon = PLAN_ICONS[iconId] || Zap;
              const iconStyle = PLAN_ICON_COLORS[iconId] || PLAN_ICON_COLORS.basico;
              const isPopular = plan.is_popular;

              return (
                <div
                  key={plan.id || i}
                  className={`relative rounded-2xl p-8 flex flex-col ${
                    isPopular
                      ? 'bg-[#111] border-2 border-[#FF3131]'
                      : 'bg-[#1C1C1C] border border-[#333]'
                  }`}
                  style={{ marginTop: isPopular ? '-12px' : '0' }}
                  data-testid={`plan-${plan.id || i}`}
                >
                  {/* MÁS POPULAR badge */}
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF3131] text-white px-5 py-1 rounded-full text-sm font-bold flex items-center gap-1 whitespace-nowrap">
                      <Star className="w-3.5 h-3.5" />
                      MÁS POPULAR
                    </div>
                  )}

                  {/* Icon + Name */}
                  <div className="flex items-center gap-4 mb-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: iconStyle.bg }}
                    >
                      <PlanIcon className="w-6 h-6" style={{ color: iconStyle.text }} />
                    </div>
                    <h3
                      className="text-xl font-black"
                      style={{ color: isPopular ? '#FF3131' : '#E9FF60' }}
                    >
                      {plan.name}
                    </h3>
                  </div>

                  <p className="text-sm text-gray-400 mb-6">{plan.description}</p>

                  {/* Price */}
                  <div className="mb-6">
                    <span
                      className="text-5xl font-black"
                      style={{ color: isPopular ? '#FF3131' : '#E9FF60' }}
                    >
                      {formatPrice(plan)}
                    </span>
                    <span className="text-sm text-gray-400 ml-2">{currency}/mes</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    {(plan.features || []).map((feature, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <CheckCircle2
                          className="w-5 h-5 flex-shrink-0 mt-0.5"
                          style={{ color: isPopular ? '#FF3131' : '#E9FF60' }}
                        />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Button — link changes per currency */}
                  <a
                    href={getButtonLink(plan)}
                    className="block w-full py-3.5 rounded-full text-center font-bold transition-all hover:opacity-90 hover:scale-[1.02]"
                    style={
                      isPopular
                        ? { backgroundColor: '#FF3131', color: 'white' }
                        : { backgroundColor: '#E9FF60', color: 'black' }
                    }
                    data-testid={`plan-${plan.id || i}-button`}
                  >
                    {plan.button_text || 'Solicitar Ahora'}
                  </a>
                </div>
              );
            })}
          </div>

          {/* Guarantee */}
          <div className="bg-[#1C1C1C] border border-[#333] rounded-2xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#E9FF60] flex items-center justify-center">
                <Shield className="w-8 h-8 text-black" />
              </div>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-[#E9FF60]" data-testid="guarantee-title">
              {guarantee.title || 'Garantía de Satisfacción'}
            </h3>
            <p className="text-gray-300 mb-4">
              {guarantee.description || 'Si no estás satisfecho en los primeros 30 días, te devolvemos el 100% de tu dinero. Sin preguntas, sin complicaciones.'}
            </p>
            <p className="text-sm text-gray-400">
              {guarantee.contact_text || '¿Tenés dudas?'}{' '}
              <a
                href={`https://wa.me/${settings.whatsapp_number || '3425149602'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E9FF60] hover:underline inline-flex items-center gap-1"
              >
                <MessageCircle className="w-4 h-4" />
                Contactanos
              </a>
            </p>
          </div>
        </div>
      </div>

      <AgencyFooter />
    </div>
  );
};

export default PlanesPage;
